import type { ReactElement, ReactNode } from 'react';
import { Panel, PanelHeader, Skeleton } from '@/components/ui';
import { cx } from '@/lib/cx';
import { formatDecimalString } from '@/lib/format';
import { compactMoneyParts } from '@/lib/targets';
import { CompactFigure, TILE_FIELD_LABEL } from '@/components/dashboards/TargetTiles';
import type { RevenueCurrency } from '@/lib/use-revenue-analytics';
import { medianAmountOf, shareText, topShareOf, type ScopedRow } from './revenue-company-shared';

/**
 * THE FOUR HEADLINE FIGURES — how much, from how many, how typical, how
 * concentrated.
 *
 * ⚠️ **THE VISUAL IDIOM IS `AccountsStatCards`', DELIBERATELY**: one hairline
 * grid (`gap-px` over `bg-border`, cells painted `bg-surface`, so the 1px gaps
 * read as rules), the same `TILE_FIELD_LABEL` and the same `text-3xl` figure.
 * `TILE_FIELD_LABEL` is IMPORTED rather than restated so a third dashboard
 * cannot drift on a label style — which is the same call that file made.
 *
 * ⚠️ **READ-OUTS, NOT CONTROLS.** Nothing here is clickable, so unlike the
 * Accounts cards these are `<div>`/`<p>` rather than `<button>`/`<span>`. The
 * cycle strip below is where this tab puts its clickable summary; a row of
 * cards that look like the Accounts ones and do not respond would teach a
 * reader that the Accounts ones do not either.
 *
 * ── 🔴 WHAT THESE FOUR FIGURES ARE ALLOWED TO CLAIM ────────────────────────
 *
 * Every one of them is **one currency, one cycle scope, paid invoices, including
 * tax** (owner, 2026-10-16; it was ex-tax).
 * None of them is "revenue" without those four qualifiers, which is why the
 * currency code is rendered against the money rather than being left to the
 * control that set it — a figure read out of context, screenshotted or pasted,
 * has to carry its own unit.
 *
 * ⚠️ **A MISSING FIGURE IS AN EM DASH AND NEVER A ZERO.** In this system "no
 * revenue in this currency" and "revenue of exactly zero" are different facts
 * and only one of them is news. `NoFigure` below is the only way a tile renders
 * without a number.
 *
 * ⛔ **FOUR TILES, AND THE COUNT DIVIDES ITS BREAKPOINTS EXACTLY.** The grid is
 * `gap-px` over `bg-border`, so an EMPTY CELL is not whitespace — it is a
 * visible break in the frame, which reads as a tile that failed to render.
 * `1 → 2 → 4` is exact at four; adding a fifth means re-cutting the counts, not
 * adding a breakpoint.
 *
 * ── 🔴 FOUR TILES, FOUR QUESTIONS — AND ONE OF THEM USED TO BE A RESTATEMENT ─
 *
 * The tiles read **total · count · typical · concentration**, and each answers
 * something the other three cannot:
 *
 *   Paid revenue    how much money           an exact sum
 *   Companies       from how many accounts   a count
 *   Median account  how big a typical one is the middle of the distribution
 *   Top 10 share    how concentrated it is   the shape of the head
 *
 * ⚠️ **THE THIRD TILE USED TO BE *Largest account*, AS A SHARE, AND IT WAS THE
 * SAME QUESTION AS THE FOURTH.** The largest account is a member of the top
 * ten, so both tiles measured concentration and the row answered three
 * questions wearing four labels. The median is the missing one: a large total
 * with a high top-ten share and a small median is a long tail, and no other
 * element on this tab shows that.
 *
 * ⚠️ **NOTHING WAS LOST WITH IT.** The largest account is row 1 of the ranked
 * table immediately below, carrying its NAME, its exact figure and its share —
 * all three, un-truncated, and one glance away. The tile was a lossier copy of
 * a row that is always on screen.
 *
 * ⛔ **AND THE MEDIAN IS NOT AN AVERAGE.** Total ÷ companies is derivable from
 * the two tiles beside it, so it would restate them; and on a set this skewed
 * it describes nobody. See `medianAmountOf` for why an even count takes the
 * lower of the two middles rather than averaging them into a figure no account
 * was ever paid.
 */

/** The headline figure style, shared so four tiles cannot drift. */
const FIGURE = 'tabular block text-2xl font-semibold text-fg';

/**
 * What a tile shows instead of a number.
 *
 * The `note` names the state rather than explaining it — "No USD revenue" is a
 * measurement, and it is the exception the no-helper-text rule already makes
 * for a fact the reader cannot otherwise see. A bare dash with nothing beside
 * it reads as a failed read.
 */
function NoFigure({ note }: { note: string }): ReactElement {
  return (
    <>
      <span className={cx(FIGURE, 'text-fg-subtle')}>—</span>
      <span className="mt-0.5 block text-xs text-fg-subtle">{note}</span>
    </>
  );
}

/**
 * A headline amount with its unit.
 *
 * The currency code sits BEFORE the figure in `text-2xs`, matching
 * `TotalFigure` on `/revenue-tracker` so one company's number reads the same
 * way on both screens. It is never omitted: a bare total on a tab whose whole
 * design is one-currency-at-a-time invites exactly the cross-currency addition
 * the server refuses to make.
 *
 * ⚠️ **ONE COMPONENT FOR BOTH MONEY TILES**, so the total and the median cannot
 * come to render their unit two different ways.
 *
 * 🔴 **COMPACTED WITH K/M SINCE 2026-09-13 — owner's decision, and it applies to
 * every money figure on the Management Dashboard.** The digits come from
 * `compactMoneyParts`, which is the ONE compact formatter in this app: a second
 * one would agree the day it was written and drift on the first correction, and
 * what would drift is how the same money reads on this tab versus the three
 * beside it.
 *
 * ⛔ **THE EXACT DECIMAL SURVIVES ON THE `title`, AND THAT IS NOT OPTIONAL.** A
 * compacted figure has thrown away everything below the hundred thousand, so two
 * totals £40,000 apart both render `£1.20M`. Without the `title` the precise
 * number exists on the page in no form at all — and this tile is the most
 * screenshotted figure on the tab. The currency code travels with it for the
 * same reason it is rendered beside the number: a figure read out of context has
 * to carry its own unit.
 *
 * ⚠️ **NEVER PARSE THE COMPACTED STRING BACK AND NEVER COMPARE TWO OF THEM.**
 * The total and the median are both passed in as exact decimal strings and every
 * piece of arithmetic on this tab — the sum, the shares, the median pick — reads
 * those, never this.
 *
 * ⚠️ **ONLY THE `K`/`M` IS GOLD.** Not the currency code, not the digits, not the
 * separators. `CompactFigure` is imported rather than restated for exactly the
 * reason `TILE_FIELD_LABEL` is: it carries the no-whitespace rule that keeps the
 * three pieces one figure to a screen reader and to a selection, and a second
 * copy is where that rule gets lost.
 */
function TileMoney({
  amount,
  currency,
}: {
  amount: string;
  currency: RevenueCurrency;
}): ReactElement {
  return (
    <span
      className="flex items-baseline gap-1.5"
      title={`${formatDecimalString(amount)} ${currency}`}
    >
      {/* No code span: it read `USD US$8.51M`. See `Money` in
          `RevenueCompanyTable.tsx` for the full reasoning and the one condition
          that would bring it back — a table able to hold two currencies. */}
      <span className={FIGURE}>
        <CompactFigure parts={compactMoneyParts(amount, currency)} />
      </span>
    </span>
  );
}

/** One cell of the hairline grid. */
function Tile({
  label,
  loading,
  children,
}: {
  label: string;
  loading: boolean;
  children: ReactNode;
}): ReactElement {
  return (
    <li className="flex min-w-0">
      <div className="flex min-w-0 flex-1 flex-col bg-surface px-4 py-4">
        <p className={cx('m-0', TILE_FIELD_LABEL)}>{label}</p>
        {/*
         * `mt-auto` bottom-aligns the figures across the row, so a label that
         * wraps at one width cannot leave one number sitting a line below its
         * neighbours.
         */}
        <div className="mt-auto block pt-2">
          {loading ? <Skeleton className="h-9 w-24" /> : children}
        </div>
      </div>
    </li>
  );
}

export function RevenueHeadline({
  scoped,
  total,
  currency,
  scopeLabel,
  loading,
}: {
  /** The companies currently on screen, ranked largest first. */
  scoped: readonly ScopedRow[];
  /**
   * The exact sum of `scoped`.
   *
   * ⚠️ **PASSED IN RATHER THAN RE-SUMMED HERE, so this tile and the share
   * column in the table below are literally the same figure.** Two calls to one
   * pure function cannot disagree today; what they can do is come apart the day
   * somebody changes what "the total" is scoped to in one caller and not the
   * other, and a headline that does not equal the shares under it is the kind
   * of disagreement a reader takes to mean the page is broken.
   */
  total: string | null;
  currency: RevenueCurrency;
  /** What the reader chose, for the heading — "All cycles" or a cycle's name. */
  scopeLabel: string;
  loading: boolean;
}): ReactElement {
  const topTenShare = topShareOf(scoped, 10, total);
  const median = medianAmountOf(scoped);

  return (
    <Panel className="overflow-hidden">
      {/*
       * ⚠️ **THE UNIT AND THE POPULATION, IN THE PANEL'S OWN HEADING.** Every
       * panel on this tab repeats those qualifiers rather than relying on the
       * one above it, and the repetition ACROSS panels is deliberate where
       * repetition WITHIN one is what gets removed: a row of tiles is the most
       * screenshotted thing on a dashboard, and a figure pasted into a message
       * has to carry its own unit.
       */}
      <PanelHeader
        title="Summary"
        description={`Paid, incl. tax · ${currency} · ${scopeLabel}`}
      />

      <ul
        aria-label="Revenue by company summary"
        className="m-0 grid list-none grid-cols-1 gap-px bg-border p-0 sm:grid-cols-2 lg:grid-cols-4"
      >
        <Tile label="Paid revenue" loading={loading}>
          {total === null ? (
            <NoFigure note={`No ${currency} revenue in scope`} />
          ) : (
            <TileMoney amount={total} currency={currency} />
          )}
        </Tile>

        <Tile label="Companies" loading={loading}>
          <span className={FIGURE}>{scoped.length.toLocaleString()}</span>
        </Tile>

        {/*
         * ⚠️ **"Median", NOT "Average".** The word is the contract: an average
         * on this set is pulled up by one account at a tenth of everything and
         * would describe nobody, and it is derivable from the two tiles to the
         * left. See `medianAmountOf`.
         */}
        <Tile label="Median account" loading={loading}>
          {median === null ? (
            <NoFigure note="No companies in scope" />
          ) : (
            <TileMoney amount={median} currency={currency} />
          )}
        </Tile>

        <Tile label="Top 10 share" loading={loading}>
          {topTenShare === null ? (
            /*
             * ⚠️ **A DASH RATHER THAN 100%.** With ten companies or fewer the
             * top ten are all of them, so the figure would be true, meaningless
             * and read as extreme concentration. The note names which of the
             * two reasons applies.
             */
            <NoFigure
              note={
                scoped.length === 0
                  ? 'No companies in scope'
                  : `Only ${scoped.length.toLocaleString()} in scope`
              }
            />
          ) : (
            <span className={FIGURE}>{shareText(topTenShare)}</span>
          )}
        </Tile>
      </ul>
    </Panel>
  );
}
